import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const variantLabel = (li) => Object.values(li.variant_attributes || {}).filter(Boolean).join(" / ") || li.variant_sku || "Standart";

// Stok için Üretim — beden/varyant bazında kısmi "Stoka Al"
export function FinishToStockDialog({ order, onClose, onDone }) {
  const lines = useMemo(() => (order?.line_items || []).filter((li) => li.id && li.product_id), [order]);
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState("");

  useEffect(() => {
    if (!order) return;
    setQty(Object.fromEntries(lines.map((li) => [li.id, Math.max(0, (li.quantity || 0) - (li.stocked_quantity || 0))])));
    setKey(crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`);
  }, [order, lines]);

  const total = Object.values(qty).reduce((a, b) => a + (Number(b) || 0), 0);
  const submit = async () => {
    setBusy(true);
    try {
      const quantities = Object.fromEntries(Object.entries(qty).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)]));
      const { data } = await api.post(`/orders/${order.id}/finish-to-stock`, { quantities, request_id: key });
      toast.success(data.duplicate ? "Bu giriş zaten kaydedilmişti" : `${order.order_no} → ${total} adet stoğa alındı${data.completed ? " (emir tamamlandı)" : ""}`);
      onDone?.(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-md" data-testid="finish-stock-dialog">
        <DialogHeader><DialogTitle>Stoka Al · {order?.order_no}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {lines.map((li) => {
            const rem = Math.max(0, (li.quantity || 0) - (li.stocked_quantity || 0));
            return (
              <div key={li.id} className="flex items-center gap-3 text-sm" data-testid={`finish-stock-line-${li.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="truncate">{variantLabel(li)}</div>
                  <div className="text-[11px] text-slate-500 font-mono">Plan {li.quantity} · Stokta {li.stocked_quantity || 0} · Kalan {rem}</div>
                </div>
                <Input type="number" min={0} max={rem} disabled={!rem} value={qty[li.id] ?? 0}
                  onChange={(e) => setQty((q) => ({ ...q, [li.id]: Math.min(rem, Math.max(0, parseInt(e.target.value || "0", 10))) }))}
                  className="w-24 h-9 bg-slate-950 border-slate-800 rounded-sm text-right" data-testid={`finish-stock-qty-${li.id}`} />
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm border-slate-700" onClick={onClose}>Vazgeç</Button>
          <Button className="rounded-sm bg-cyan-600 hover:bg-cyan-500" disabled={!total || busy} onClick={submit} data-testid="finish-stock-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `${total} Adet Stoğa Al`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
